/**
 * `ParticipantRoleService` — `/api/v1/events/{eventId}/participant-roles/*`.
 *
 * ⚠️ **ЭТО НЕ ПРАВА ДОСТУПА.** Роль участника — то, кем человек приехал на
 * соревнование («спортсмен», «тренер», «представитель команды»); она ничего не
 * разрешает и ничего не запрещает. Права описывает `MemberRole`
 * (владелец/наблюдатель), и эти два понятия не пересекаются нигде: ни в
 * проверках, ни в таблицах, ни в токене. Не поддавайтесь соблазну считать
 * что-либо из этого справочника при рисовании кнопок.
 *
 * Справочник **локален** для мероприятия: общего списка ролей на систему нет и
 * не подразумевается — у каждого соревнования свой состав, и одна «общая»
 * запись, которую правит владелец одного мероприятия, меняла бы справочник
 * всем остальным.
 *
 * ПРАВА те же, что и на мероприятие: admin и owner наполняют, viewer читает,
 * не участник получает 404 `EVENT_NOT_FOUND`.
 */

import { http } from '@/lib/http'
import type {
  CreateParticipantRoleRequest,
  CreateParticipantRoleResponse,
  DeleteParticipantRoleResponse,
  ListParticipantRolesResponse,
  SetParticipantRoleAccessLevelsRequest,
  SetParticipantRoleAccessLevelsResponse,
  SetParticipantRolePictogramsRequest,
  SetParticipantRolePictogramsResponse,
  SetParticipantRoleAccessZonesRequest,
  SetParticipantRoleAccessZonesResponse,
  UpdateParticipantRoleRequest,
  UpdateParticipantRoleResponse,
} from '@/types/api'

function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/participant-roles${suffix}`
}

export const participantRolesApi = {
  /**
   * `GET .../participant-roles` — весь справочник, по алфавиту. Читает любой
   * участник мероприятия.
   *
   * Пагинации нет намеренно: это десятки записей, выбираемых из выпадающего
   * списка, а не лента. Постраничный справочник заставил бы клиента листать,
   * чтобы собрать `<Select>` целиком.
   */
  async list(eventId: string, signal?: AbortSignal): Promise<ListParticipantRolesResponse> {
    const { data } = await http.get<ListParticipantRolesResponse>(path(eventId), { signal })
    return data
  },

  /**
   * `POST .../participant-roles` — admin или owner.
   * `409 PARTICIPANT_ROLE_ALREADY_EXISTS` с `metadata.name` — название занято
   * (сравнение без учёта регистра).
   *
   * Цвет можно не выбирать: пустая строка хранится как есть и означает «цвет не
   * задан» — такая роль печатается на бейдже цветом из макета. Регистр сервер
   * опускает сам.
   */
  async create(
    eventId: string,
    body: CreateParticipantRoleRequest,
  ): Promise<CreateParticipantRoleResponse> {
    const { data } = await http.post<CreateParticipantRoleResponse>(path(eventId), body)
    return data
  },

  /**
   * `PUT .../participant-roles/{id}` — название и цвет.
   *
   * PUT, а не PATCH: изменяемых полей два, оба всегда есть в форме, и роли с
   * названием, но без цвета не бывает — пустая строка означает «цвет по
   * умолчанию», а не «не менять».
   *
   * ⚠️ Зоны сюда не входят: их правят отдельным экраном и отдельным запросом.
   */
  async update(
    eventId: string,
    id: string,
    body: UpdateParticipantRoleRequest,
  ): Promise<UpdateParticipantRoleResponse> {
    const { data } = await http.put<UpdateParticipantRoleResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
      body,
    )
    return data
  },

  /**
   * `PUT .../participant-roles/{id}/access-zones` — набор зон роли целиком.
   *
   * Отдельный эндпоинт от переименования намеренно: одним PUT правка названия
   * отправляла бы вместе с собой весь набор зон и затирала бы чужую
   * одновременную правку набора.
   *
   * `404 ACCESS_ZONE_NOT_FOUND` — среди id есть зона не из этого мероприятия
   * (или уже удалённая). `400 VALIDATION_FAILED` с `metadata.field` вида
   * `accessZoneIds[2]` — один из id не uuid.
   */
  async setAccessZones(
    eventId: string,
    id: string,
    body: SetParticipantRoleAccessZonesRequest,
  ): Promise<SetParticipantRoleAccessZonesResponse> {
    const { data } = await http.put<SetParticipantRoleAccessZonesResponse>(
      path(eventId, `/${encodeURIComponent(id)}/access-zones`),
      body,
    )
    return data
  },

  /**
   * `PUT .../participant-roles/{id}/access-levels` — набор уровней роли
   * целиком.
   *
   * ⚠️ Третий отдельный эндпоинт роли, а не «ещё одно поле» ни к
   * переименованию, ни к зонам. Причина та же, что развела название и зоны:
   * один запрос, везущий сразу всё, затирал бы чужую одновременную правку
   * любого из наборов. И уровни зонам не альтернатива — их правят рядом, но
   * независимо.
   *
   * `404 ACCESS_LEVEL_NOT_FOUND` — среди id есть уровень не из этого
   * мероприятия (или уже удалённый). `400 VALIDATION_FAILED` с
   * `metadata.field` вида `accessLevelIds[2]` — один из id не uuid.
   */
  async setAccessLevels(
    eventId: string,
    id: string,
    body: SetParticipantRoleAccessLevelsRequest,
  ): Promise<SetParticipantRoleAccessLevelsResponse> {
    const { data } = await http.put<SetParticipantRoleAccessLevelsResponse>(
      path(eventId, `/${encodeURIComponent(id)}/access-levels`),
      body,
    )
    return data
  },

  /**
   * `PUT .../participant-roles/{id}/pictograms` — набор пиктограмм роли
   * целиком.
   *
   * ⚠️ Четвёртый отдельный эндпоинт роли. Причина та же, что развела название,
   * зоны и уровни: один запрос, везущий сразу всё, затирал бы чужую
   * одновременную правку любого из наборов.
   *
   * `404 PICTOGRAM_NOT_FOUND` — среди id есть пиктограмма не из этого
   * мероприятия (или уже удалённая). `400 VALIDATION_FAILED` с
   * `metadata.field` вида `pictogramIds[2]` — один из id не uuid.
   */
  async setPictograms(
    eventId: string,
    id: string,
    body: SetParticipantRolePictogramsRequest,
  ): Promise<SetParticipantRolePictogramsResponse> {
    const { data } = await http.put<SetParticipantRolePictogramsResponse>(
      path(eventId, `/${encodeURIComponent(id)}/pictograms`),
      body,
    )
    return data
  },

  /**
   * `DELETE .../participant-roles/{id}` — жёсткое удаление.
   *
   * Привязки к зонам и уровням уходят вместе с ролью, **сами зоны и уровни
   * остаются**: связь — свойство роли, а не справочника.
   *
   * ⚠️ У участников, которым эта роль назначена, ссылка **обрывается**: роль
   * становится «не назначена». Подтверждение обязано это сказать — иначе
   * человек узнаёт об этом по пустой колонке в стартовом протоколе.
   */
  async remove(eventId: string, id: string): Promise<DeleteParticipantRoleResponse> {
    const { data } = await http.delete<DeleteParticipantRoleResponse>(
      path(eventId, `/${encodeURIComponent(id)}`),
    )
    return data
  },
}
